import {
  at,
  flow,
  find,
  isEmpty,
  isEqual,
  get,
  getOr,
  pick,
  omit,
  extend,
} from 'lodash/fp';
import {
  replace as replaceRoute,
  push as pushRoute,
} from 'connected-react-router';

import api from 'store/actions/api';
import { getFormValues, change as changeForm } from 'redux-form';
import { actions as layoutActions } from 'components/page/Layout';
import { track, hitTypes } from 'helpers/tracking';
import { globalPromoCmsDuck } from 'helpers/globalPromo';
import { trackCid } from 'store/actions/trackCid';
import {
  sendRemovedFavoriteNotification,
  sendAddedFavoriteNotification,
  sendDislikeAdNotification,
} from 'components/Notification/actions';
import { reducer as sellWidgetDuck } from 'components/SellWidget/duck';
import { maybeLoadModelSubType } from 'components/forms/filters/vehicle/actions';
import {
  STANDARD_PRICE_ASC,
  GEOLOCATION_DISTANCE_ASC,
} from 'helpers/fieldOptions/ad/sort';
import mutateAdFromList from 'helpers/ad/mutateAdFromList';
import { getTrackingId, getUser } from 'components/user/selectors';
import { DOMAIN_PREFIX } from 'helpers/fieldOptions/domainPrefix';

import {
  loadLocationFromStorage,
  saveLocationToStorage,
} from './lastSearchLocation';
import formToMeta from '../../mappers/formToMeta';
import {
  SEARCH_ADS_FORM,
  EXTRA_FILTERS_QUERY_KEY,
  EXACT_SEARCH_TYPE,
} from '../constants';
import parseAdsSearch from '../../parseAdsSearch';
import stringifyAdsQuery from '../../stringifyAdsQuery';
import {
  AIRBAG_NAME,
  BODY_TYPE_NAME,
  COLOR_NAME,
  COMFORT_NAME,
  CONSUMPTION_RANGE_NAME,
  DOOR_RANGE_NAME,
  EMISSION_STICKER_NAME,
  ENGINE_RANGE_NAME,
  ENV_STANDARD_NAME,
  EXTRAS_NAME,
  FUEL_TYPE_NAME,
  GEOLOCATION_NAME,
  HAS_IMAGES_NAME,
  INSPECTION_EXPIRY_NAME,
  INTERIOR_NAME,
  LIGHT_NAME,
  METALLIC_NAME,
  MILEAGE_RANGE_NAME,
  MONTHLY_PRICE_RANGE_NAME,
  MULTIMEDIA_NAME,
  NOT_DAMAGED_NAME,
  PAGE_NAME,
  PARKING_SENSORS_NAME,
  POWER_RANGE_NAME,
  PREV_OWNER_NAME,
  PRICE_RANGE_NAME,
  SEAT_COMFORT_NAME,
  SEAT_RANGE_NAME,
  SECURITY_NAME,
  SELLER_TYPE_NAME,
  SERVICE_HISTORY_NAME,
  SMOKER_NAME,
  SORT_NAME,
  TRANSMISSION_NAME,
  UPHOLSTERY_NAME,
  VEHICLE_NAME,
  YEAR_RANGE_NAME,
} from '../../constants';

import { adsDuck, paramsDuck, uiDuck } from '../duck';
import {
  setExpiryOfFiltersApplied,
  getExpiryOfFiltersApplied,
} from './appliedFiltersExpiry';

const getSearchAdsResult = get(['searchLayout', 'ads', 'result']);

const getResultsAmount = get([
  'searchLayout',
  'ads',
  'result',
  'pagination',
  'resultsAmount',
]);

const getExactAdsCount = get([
  'searchLayout',
  'ads',
  'result',
  'exactAdsCount',
]);

const getIsSortTouched = get(['searchLayout', 'ui', 'isSortTouched']);

const getSearchAdsFormValues = flow(
  getFormValues(SEARCH_ADS_FORM),
  (value = {}) => value,
);

const isSortedByGeolocation = flow(
  get([SORT_NAME]),
  isEqual(GEOLOCATION_DISTANCE_ASC),
);

const shouldResetGeoLocationSort = flow(
  at([SORT_NAME, GEOLOCATION_NAME]),
  ([sort, geoLocation]) =>
    sort === GEOLOCATION_DISTANCE_ASC && isEmpty(geoLocation),
);

const mayBeTrackNoResults = () =>
  function* ({ getState }) {
    const exactAdsCount = getExactAdsCount(getState());
    const expiryOfFiltersApplied = getExpiryOfFiltersApplied(getState());
    const needsToTrack = Date.now() < expiryOfFiltersApplied;

    if (exactAdsCount === 0 && needsToTrack) {
      yield track(hitTypes.SEARCH_WITH_FILTERS_NO_RESULTS, {});
    }
  };

export const openExtraFilters = (beforeRedirection) =>
  function* ({ getCurrentLocation }) {
    const search = flow(
      getCurrentLocation,
      get(['search']),
      parseAdsSearch,
      extend({ [EXTRA_FILTERS_QUERY_KEY]: '1' }),
      stringifyAdsQuery,
    )();

    let isRedirected = false;

    if (beforeRedirection) {
      const result = yield beforeRedirection(search);

      isRedirected = result.isRedirected;
    }

    if (!isRedirected) {
      global.window.scrollTo(0, 0);
      yield pushRoute({ search });
    }
  };

export const removeAllExtraFilters = () =>
  function* () {
    yield [
      changeForm(SEARCH_ADS_FORM, AIRBAG_NAME, null),
      changeForm(SEARCH_ADS_FORM, BODY_TYPE_NAME, null),
      changeForm(SEARCH_ADS_FORM, COLOR_NAME, null),
      changeForm(SEARCH_ADS_FORM, COMFORT_NAME, null),
      changeForm(SEARCH_ADS_FORM, CONSUMPTION_RANGE_NAME, null),
      changeForm(SEARCH_ADS_FORM, DOOR_RANGE_NAME, null),
      changeForm(SEARCH_ADS_FORM, EMISSION_STICKER_NAME, null),
      changeForm(SEARCH_ADS_FORM, ENGINE_RANGE_NAME, null),
      changeForm(SEARCH_ADS_FORM, ENV_STANDARD_NAME, null),
      changeForm(SEARCH_ADS_FORM, EXTRAS_NAME, null),
      changeForm(SEARCH_ADS_FORM, FUEL_TYPE_NAME, null),
      changeForm(SEARCH_ADS_FORM, GEOLOCATION_NAME, {}),
      changeForm(SEARCH_ADS_FORM, HAS_IMAGES_NAME, null),
      changeForm(SEARCH_ADS_FORM, INSPECTION_EXPIRY_NAME, null),
      changeForm(SEARCH_ADS_FORM, INTERIOR_NAME, null),
      changeForm(SEARCH_ADS_FORM, LIGHT_NAME, null),
      changeForm(SEARCH_ADS_FORM, METALLIC_NAME, null),
      changeForm(SEARCH_ADS_FORM, MILEAGE_RANGE_NAME, null),
      changeForm(SEARCH_ADS_FORM, MONTHLY_PRICE_RANGE_NAME, null),
      changeForm(SEARCH_ADS_FORM, MULTIMEDIA_NAME, null),
      changeForm(SEARCH_ADS_FORM, NOT_DAMAGED_NAME, null),
      changeForm(SEARCH_ADS_FORM, PARKING_SENSORS_NAME, null),
      changeForm(SEARCH_ADS_FORM, POWER_RANGE_NAME, null),
      changeForm(SEARCH_ADS_FORM, PREV_OWNER_NAME, null),
      changeForm(SEARCH_ADS_FORM, PRICE_RANGE_NAME, null),
      changeForm(SEARCH_ADS_FORM, SEAT_COMFORT_NAME, null),
      changeForm(SEARCH_ADS_FORM, SEAT_RANGE_NAME, null),
      changeForm(SEARCH_ADS_FORM, SECURITY_NAME, null),
      changeForm(SEARCH_ADS_FORM, SELLER_TYPE_NAME, null),
      changeForm(SEARCH_ADS_FORM, SERVICE_HISTORY_NAME, null),
      changeForm(SEARCH_ADS_FORM, SMOKER_NAME, null),
      changeForm(SEARCH_ADS_FORM, TRANSMISSION_NAME, null),
      changeForm(SEARCH_ADS_FORM, UPHOLSTERY_NAME, null),
      changeForm(SEARCH_ADS_FORM, VEHICLE_NAME, []),
      changeForm(SEARCH_ADS_FORM, YEAR_RANGE_NAME, null),
    ];
  };

export const removeExtraFiltersItem = ({ name, form }) =>
  function* () {
    const value = getOr(null, [name], form);

    yield changeForm(SEARCH_ADS_FORM, name, value);
  };

const getSearchType = () =>
  function* ({ getState, getCurrentLocation }) {
    const { search } = getCurrentLocation();

    if (search) {
      return 'custom';
    }

    return get(['searchLayout', 'params', 'userSearchType'], getState());
  };

const getFromProperty = (pageSize, currentPage = 1) =>
  (currentPage - 1) * pageSize;

const getMeta = (meta) =>
  function* ({ getState, getCurrentLocation }) {
    const numberOfResultsPerPage = get(
      ['searchLayout', 'params', 'numberOfResultsPerPage'],
      getState(),
    );

    if (numberOfResultsPerPage) {
      const pageQueryParam = flow(
        get(['search']),
        parseAdsSearch,
        get(['page']),
      )(getCurrentLocation());

      const currentPageNumber =
        get(['page'], getSearchAdsFormValues(getState())) || pageQueryParam;
      const fromProperty = getFromProperty(
        numberOfResultsPerPage,
        currentPageNumber,
      );

      return {
        ...meta,
        size: numberOfResultsPerPage,
        from: fromProperty,
      };
    }

    return meta;
  };

const loadAds = (meta, { forceLoad = false } = {}) =>
  function* ({ getState, getCurrentLocation }) {
    const { trackingId } = getUser(getState());
    const {
      isLoaded,
      result: { meta: currentMeta },
    } = get(['searchLayout', 'ads'], getState());

    if (!forceLoad) {
      if (isLoaded && isEqual(meta, currentMeta)) {
        return null;
      }
    }

    const userSearchType = yield getSearchType();

    yield adsDuck.loadAds({
      meta,
      trackingId,
      userSearchType,
      type: EXACT_SEARCH_TYPE,
    });

    const { currentPage, pageNumber } = get(
      ['searchLayout', 'ads', 'result', 'pagination'],
      getState(),
    );

    const page = Number(currentPage);
    const isPageIncorrect = page > 1 && page > pageNumber;

    if (isPageIncorrect) {
      const search = flow(
        get(['search']),
        parseAdsSearch,
        omit(['page']),
        stringifyAdsQuery,
      )(getCurrentLocation());

      yield replaceRoute({ search });
    }
  };

const mayBeResetSortTouched = () =>
  function* ({ getState }) {
    const searchAdsFormValues = getSearchAdsFormValues(getState());

    if (shouldResetGeoLocationSort(searchAdsFormValues)) {
      yield uiDuck.set({ isSortTouched: false });
    }
  };

export const saveLastSearch = (key) =>
  function* ({ location }) {
    // Only save search if we are on main search page.
    const shouldSaveSearch = location.pathname === `/${DOMAIN_PREFIX}/`;

    if (shouldSaveSearch) {
      yield saveLocationToStorage(key);
    }
  };

export const restoreLastSearch = (storageKey) =>
  function* ({ getCurrentLocation, referer }) {
    const { search } = getCurrentLocation();

    if (referer.isCurrentHost() && !search) {
      const lastSearchLocation = yield loadLocationFromStorage(storageKey);

      if (lastSearchLocation) {
        yield replaceRoute(lastSearchLocation);
      }
    }

    yield saveLastSearch(storageKey);
  };

export const closeModalFilters = () =>
  function* () {
    yield uiDuck.set({ isModalFiltersActive: false });
  };

export const showModalFilters = () =>
  function* () {
    yield uiDuck.set({ isModalFiltersActive: true });
  };

export const applyFilters = ({
  changeFilters,
  form,
  withModifiedSort = true,
  resetPage = true,
  searchWithFilters = true,
}) =>
  function* ({ getState }) {
    form = form || getSearchAdsFormValues(getState());

    if (resetPage) {
      form = extend(form, { [PAGE_NAME]: 1 });
    }

    if (withModifiedSort) {
      const isSortTouched = getIsSortTouched(getState());

      form = !isSortTouched
        ? extend(form, { [SORT_NAME]: STANDARD_PRICE_ASC })
        : form;
    }

    const result = yield changeFilters(form);
    const { query } = result || {};

    if (!query) {
      return;
    }

    const resultsAmount = getResultsAmount(getState());

    const trackingEvent = searchWithFilters
      ? hitTypes.SEARCH_WITH_FILTERS
      : hitTypes.SEARCH_WITH_FORM;

    yield track(trackingEvent, {
      searchParams: query,
      resultsAmount,
    });

    yield setExpiryOfFiltersApplied();
  };

export const applyFiltersByModalSubmit = (changeFilters) =>
  function* () {
    yield closeModalFilters();
    yield mayBeResetSortTouched();
    yield applyFilters({ changeFilters });
  };

export const applyFiltersByChangedValue = (changeFilters) =>
  function* ({ getState }) {
    const { isModalFiltersActive } = get(['searchLayout', 'ui'], getState());

    yield mayBeResetSortTouched();

    if (isModalFiltersActive) {
      return;
    }

    yield applyFilters({ changeFilters });
  };

export const applyFiltersByAdvSearchSubmit = ({ changeFilters }) =>
  function* () {
    yield mayBeResetSortTouched();
    yield applyFilters({ changeFilters });
  };

export const applySort = (changeSort) =>
  function* () {
    yield uiDuck.set({ isSortTouched: true });
    yield applyFilters({
      changeFilters: changeSort,
      withModifiedSort: false,
      resetPage: true,
    });
  };

export const removeFilterItem = ({ changeFilters, form, fieldName }) =>
  function* ({ getState }) {
    const searchAdsForm = flow(pick([SORT_NAME]))(
      getSearchAdsFormValues(getState()),
    );

    // Special case
    // When when sorted by geolocation and we remove the location active filter
    // We sort to Price ASC  in this case
    if (fieldName === GEOLOCATION_NAME) {
      const hasGeolocationSorting = isSortedByGeolocation(searchAdsForm);

      if (hasGeolocationSorting) {
        yield uiDuck.set({ isSortTouched: false });
      }
    }

    yield applyFilters({ changeFilters, form });
  };

export const clearAllFilters = ({ changeFilters }) =>
  function* ({ getState }) {
    const form = flow(pick([SORT_NAME]))(getSearchAdsFormValues(getState()));
    const hasGeolocationSorting = isSortedByGeolocation(form);

    if (hasGeolocationSorting) {
      yield uiDuck.set({ isSortTouched: false });
    }

    yield applyFilters({
      changeFilters,
      form,
      withModifiedSort: isSortedByGeolocation,
    });
  };

export const loadPage = (changePage, page) =>
  function* ({ getState }) {
    const form = flow(
      getSearchAdsFormValues,
      omit([PAGE_NAME]),
      extend({ [PAGE_NAME]: page }),
    )(getState());

    const meta = yield getMeta(formToMeta()(form));

    yield loadAds(meta);
    yield applyFilters({
      changeFilters: (form) => changePage(form, page),
      form,
      resetPage: false,
      withModifiedSort: false,
    });
  };

export const destroy = () =>
  function* () {
    yield [adsDuck.reset(), paramsDuck.reset(), uiDuck.reset()];
  };

const propsUsedByActions = [
  'withInitialAdResultsAmount',
  'userSearchType',
  'numberOfResultsPerPage',
];

const getLikeDislikeCount = () =>
  function* ({ getState }) {
    const userSearchType = 'default';
    const trackingId = getTrackingId(getState());
    const meta = formToMeta()(getSearchAdsFormValues(getState()));
    const { result } = yield api.getAdsByType({
      meta: { ...meta, fields: ['id'] },
      trackingId,
      userSearchType,
      type: EXACT_SEARCH_TYPE,
    });

    return at(['likeCount', 'dislikeCount'])(result);
  };

const showToggleFavoriteNotify = (adTitle = '', isFavorite) =>
  isFavorite
    ? sendAddedFavoriteNotification(adTitle)
    : sendRemovedFavoriteNotification(adTitle);

export const toggleAdFavorite = (adId, isFavorite = true) =>
  function* ({ getState }) {
    const [likeCount, dislikeCount] = yield getLikeDislikeCount();
    const adsResult = getSearchAdsResult(getState());
    const adTitle = flow(
      get(['items']),
      find({ id: adId }),
      get(['title']),
    )(adsResult);
    const mutator = (item) => ({ ...item, isFavorite });

    if (adsResult.items && adsResult.items.length) {
      const newItems = mutateAdFromList({
        items: adsResult.items,
        id: adId,
        mutator,
      });

      yield adsDuck.set({
        result: {
          ...adsResult,
          items: newItems,
          likeCount,
          dislikeCount,
        },
      });

      showToggleFavoriteNotify(adTitle, isFavorite);
    }
  };

export const onDislikeAd = (adId, isDislike = true) =>
  function* ({ getState }) {
    const form = flow(getSearchAdsFormValues)(getState());
    const adsResult = getSearchAdsResult(getState());
    const adTitle = flow(
      get(['items']),
      find({ id: adId }),
      get(['title']),
    )(adsResult);

    const meta = yield getMeta(formToMeta()(form));

    yield loadAds(meta, { forceLoad: true });
    sendDislikeAdNotification(adTitle, isDislike);
  };

export const init = (initialProps) =>
  function* ({ getCurrentLocation }) {
    const isSortInQuery = flow(
      get(['search']),
      parseAdsSearch,
      get([SORT_NAME]),
      Boolean,
    )(getCurrentLocation());

    if (isSortInQuery) {
      yield uiDuck.set({ isSortTouched: true });
    }

    if (initialProps) {
      yield paramsDuck.set(pick(propsUsedByActions, initialProps));

      const make = get(['initialForm', 'vehicle', '0', 'make'], initialProps);
      const meta = flow(get(['initialForm']), formToMeta())(initialProps);
      const cid = get(['cms', 'seo-cid'])(initialProps);
      const updatedMeta = yield getMeta(meta);

      yield [
        globalPromoCmsDuck.tryLoad(),
        maybeLoadModelSubType(make),
        layoutActions.fetch(),
        loadAds(updatedMeta),
        sellWidgetDuck.tryLoad(),
      ];

      yield trackCid({ cid });
      yield mayBeTrackNoResults();
    } else {
      yield layoutActions.fetch();
    }
  };
